import { useEffect, useState } from "react";
import { Column } from "../../components/resultTable/resultTableType";
import { popupError } from "../../service/zendesk";
import { getOrganization, getTicket, getUser } from "../../service/ticket";
import { ISelectedItem } from "@zendeskgarden/react-dropdowns";
import {
    searchOptions,
    searchTagOptionsTicket,
    advancedTagOptionsTicket,
    searchTagOptionsUsers,
    searchTagOptionsOrganizations
} from "./searchConstants";

export type Tag = {
    ID: string,
    searchOption: string,
    value: string,
}

export function useSearchFilter() {
    const [selectedSearch, setSelectedSearch] = useState<string>('ticket');
    const [selectedTag, setSelectedTag] = useState<string>('ID')
    const [tags, setTags] = useState<Tag[]>([]);
    const [searchInput, setSearchInput] = useState<string | null>(null);
    const [searchTagOption, setSearchTagOption] = useState<ISelectedItem[]>([...searchTagOptionsTicket, ...advancedTagOptionsTicket]);
    const [totalResponses, setTotalResponses] = useState(0);
    const [columns, setColumns] = useState<Column[]>([]);
    const [results, setResults] = useState<Record<string, string>[]>([])
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage] = useState(50);
    const [pageCache, setPageCache] = useState<Record<number, Record<string, string>[]>>({});
    const [cachedTotal, setCachedTotal] = useState(0);

    function addTag() {
        if (searchInput !== null && searchInput.trim() === '') {
            setSearchInput(null);
            return;
        }
        if (searchInput !== null) {
            const values = selectedTag === "ID" ? searchInput.split(',').map(v => v.trim()).filter(v => v) : [searchInput.trim()];
            const newTags: Tag[] = values.map(value => ({
                ID: `${selectedTag}:${value}`,
                searchOption: selectedTag,
                value: value,
            }));
            setSearchInput(null);
            setTags((prevTags) => {
                const filtered = prevTags.filter(tag => !newTags.some(nt => nt.ID === tag.ID));
                return [...filtered, ...newTags];
            });
        }
    }

    function removeTag(id: string) {
        setTags((prevTags) => prevTags.filter(tag => tag.ID !== id));
    }

    function clearTags() {
        setTags([]);
    }

    function handleClickSearchOptions(option: string) {
        if (option === searchOptions[0]) // TICKET
            setSearchTagOption([...searchTagOptionsTicket, ...advancedTagOptionsTicket]);
        if (option === searchOptions[1]) // USUARIOS
            setSearchTagOption(searchTagOptionsUsers);
        if (option === searchOptions[2]) // ORGANIZACOES
            setSearchTagOption(searchTagOptionsOrganizations);

        setSelectedSearch(option)
        if (selectedSearch !== option) clearTags();
    }

    function fillColumn(search: string) {
        let col: Column[] = [];
        if (search === 'ticket')
            col = [
                { key: "ticket", label: "Ticket" },
                { key: "id", label: "ID" },
                { key: "status", label: "Status" },
                { key: "author", label: "Autor" },
                { key: "data", label: "Data de Criação" }
            ];
        if (search === 'user')
            col = [
                { key: "name", label: "Nome" },
                { key: "email", label: "Email" },
                { key: "tags", label: "Tags" },
                { key: "fuso", label: "Fuso horário" },
                { key: "data", label: "Data de Criação" }
            ];
        if (search === 'organization')
            col = [
                { key: "name", label: "Nome" },
                { key: "domain", label: "Domínio" },
                { key: "tags", label: "Tags" },
                { key: "update_date", label: "Última atualização" },
                { key: "data", label: "Data de Criação" }
            ];

        setColumns(col)
    }

    async function applyFilterTickets(updated: boolean = false, page: number = 1) {
        if (!updated && pageCache[page] && cachedTotal > 0) {
            setResults(pageCache[page]);
            setTotalResponses(cachedTotal);
            setLoading(false);
            return;
        }
        try{
            setLoading(true);
            const x = await getTicket(tags, page, perPage);
            const tickets = x["tickets"]
            let res: Record<string, string>[] = [];
            for (let i: number = 0; i < tickets.length; i++) {
                const ticket = tickets[i];
                res.push(
                    {
                        ticket: ticket["name"],
                        id: ticket["id"],
                        status: ticket["status"],
                        author: ticket["requester_name"],
                        data: ticket.creation_date.replace(
                            /(\d{4})-(\d{2})-(\d{2}).*/,
                            "$3/$2/$1"
                        )
                    }
                )
            }
            if (updated) {
                res.forEach(item => item.updated = "Sim");
            }
            setResults(res)
            setTotalResponses(x["total"]);
            setCachedTotal(x["total"]);
            setPageCache(prev => ({...prev, [page]: res}));
            setLoading(false);
        }catch(error){
            console.log(error)
            setLoading(false);
        }
    }

    async function applyFilterUser(updated: boolean = false) {
        try{
            setLoading(true);
            const x = await getUser(tags);
            const users = x["users"]
            let res: Record<string, string>[] = [];
            for (let i: number = 0; i < x["total"]; i++) {
                const user = users[i];
                res.push(
                    {
                        name: user["name"],
                        email: user["email"],
                        tags: user["tags"],
                        fuso: user["time_zone"],
                        data: user["created_at"].replace(
                            /(\d{4})-(\d{2})-(\d{2}).*/,
                            "$3/$2/$1"
                        )
                    }
                )
            }
            if (updated) {
                res.forEach(item => item.updated = "Sim");
            }
            setResults(res)
        }catch(error){
            console.log(error)
        }
    }

    async function applyFilterOrganization(updated: boolean = false) {
        try{
            setLoading(true);
            const x = await getOrganization(tags);
            const users = x["organizations"]
            let res: Record<string, string>[] = [];
            for (let i: number = 0; i < x["total"]; i++) {
                const user = users[i];
                res.push(
                    {
                        name: user["name"],
                        domain: user["domain_names"] ? user["domain_names"][0] : "",
                        tags: user["tags"],
                        update_date: user["updated_at"].replace(
                            /(\d{4})-(\d{2})-(\d{2}).*/,
                            "$3/$2/$1"
                        ),
                        data: user["created_at"].replace(
                            /(\d{4})-(\d{2})-(\d{2}).*/,
                            "$3/$2/$1"
                        )
                    }
                )
            }
            if (updated) {
                res.forEach(item => item.updated = "Sim");
            }
            setResults(res)
        }catch(error){
            console.log(error)
        }
    }

    async function applyFilter(updated: boolean = false) {
        fillColumn(selectedSearch)
        if (tags.length === 0) {
            popupError("Pelo menos uma tag", false);
            return;
        }
        setResults([]);
        setTotalResponses(0);
        setPageCache({});
        setCachedTotal(0);
        setCurrentPage(1);
        if (selectedSearch === "ticket") {
            applyFilterTickets(updated, 1);
        }
        if (selectedSearch === "user") {
            applyFilterUser(updated);
        }
        if (selectedSearch === "organization") {
            applyFilterOrganization(updated);
        }
    }

    async function changePage(page: number) {
        setCurrentPage(page);
        if (selectedSearch === "ticket") {
            await applyFilterTickets(false, page);
        }
    }

    useEffect(() => {
        if (selectedSearch === 'ticket') {
            setSearchTagOption([...searchTagOptionsTicket, ...advancedTagOptionsTicket]);
        }
    }, [selectedSearch]);

    useEffect(() => {
        setSelectedTag(searchTagOption[0].value)
    }, [searchTagOption]);

    return {
        searchOptions,
        selectedSearch,
        searchTagOption,
        selectedTag,
        setSelectedTag,
        tags,
        setTags,
        addTag,
        removeTag,
        clearTags,
        setSearchInput,
        searchInput,
        handleClickSearchOptions,
        totalResponses,
        applyFilter,
        columns,
        results,
        currentPage,
        perPage,
        changePage,
        loading
    }
}